import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import RankBadge from '../../components/RankBadge';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { 
    Loader2, Search, Users, Ban, CheckCircle, Eye, RefreshCw, AlertTriangle,
    ChevronLeft, ChevronRight, AlertCircle, History
} from 'lucide-react';
import { adminAPI } from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { toast } from 'sonner';

const getErrorMessage = (err) => {
    try {
        const detail = err?.response?.data?.detail;
        if (typeof detail === 'string') return detail;
        if (Array.isArray(detail)) return detail.map(e => e?.msg || 'Error').join('. ');
        return err?.message || 'An error occurred';
    } catch { return 'An error occurred'; }
};

const AdminUsers = () => {
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, total_pages: 0 });
    const [searchQuery, setSearchQuery] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [actionDialog, setActionDialog] = useState({ open: false, action: '', user: null });

    const loadUsers = useCallback(async (page = 1, search = searchQuery) => {
        setLoading(true);
        try {
            const res = await adminAPI.getUsersPaginated(page, pagination.limit, search);
            setUsers(res.data.users || []);
            setPagination(res.data.pagination);
        } catch (err) {
            toast.error(getErrorMessage(err));
        } finally {
            setLoading(false);
        }
    }, [pagination.limit, searchQuery]);

    useEffect(() => { loadUsers(); }, []);

    const handleSearch = (e) => {
        e.preventDefault();
        setSearchQuery(searchInput);
        loadUsers(1, searchInput);
    };

    const handleUpdateRole = async (userId, role) => {
        try {
            await adminAPI.updateUser(userId, { role });
            toast.success('User role updated');
            loadUsers();
        } catch (err) {
            toast.error(getErrorMessage(err));
        }
    };

    const handleToggleStatus = async (user) => {
        try {
            if (user.is_active) {
                await adminAPI.deactivateUser(user.id);
                toast.success('User deactivated');
            } else {
                await adminAPI.reactivateUser(user.id);
                toast.success('User reactivated');
            }
            loadUsers(pagination.page);
        } catch (err) {
            toast.error(getErrorMessage(err));
        }
        setActionDialog({ open: false, action: '', user: null });
    };

    const handleAddStrike = async (userId) => {
        try {
            await adminAPI.addStrike(userId, 'Added via admin panel');
            toast.success('Strike added');
            loadUsers(pagination.page);
        } catch (err) {
            toast.error(getErrorMessage(err));
        }
    };

    const handleRemoveStrike = async (userId) => {
        try {
            await adminAPI.removeStrike(userId);
            toast.success('Strike removed');
            loadUsers(pagination.page);
        } catch (err) {
            toast.error(getErrorMessage(err));
        }
    };

    const handleSuspend = async (userId) => {
        if (!window.confirm('Are you sure you want to suspend this user?')) return;
        try {
            await adminAPI.suspendUser(userId, 'Suspended via admin panel');
            toast.success('User suspended');
            loadUsers(pagination.page);
        } catch (err) {
            toast.error(getErrorMessage(err));
        }
    };

    // Client-side filtering for role and status (pagination returns all from search)
    const filteredUsers = users.filter(u => {
        const matchesRole = roleFilter === 'all' || u.role === roleFilter;
        const matchesStatus = statusFilter === 'all' || 
            (statusFilter === 'active' && u.is_active && !u.suspended) ||
            (statusFilter === 'deactivated' && !u.is_active) ||
            (statusFilter === 'suspended' && u.suspended);
        return matchesRole && matchesStatus;
    });

    if (loading) {
        return (
            <AdminLayout>
                <div className="flex justify-center items-center min-h-[60vh]">
                    <Loader2 className="h-12 w-12 animate-spin text-primary" />
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="p-6 lg:p-8 space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="font-heading font-extrabold text-2xl lg:text-3xl text-foreground">Users</h1>
                        <p className="text-muted-foreground">Manage contractor accounts</p>
                    </div>
                    <Button onClick={loadUsers} variant="outline" size="sm">
                        <RefreshCw className="h-4 w-4 mr-2" /> Refresh
                    </Button>
                </div>

                {/* Filters */}
                <Card className="bg-card border-border">
                    <CardContent className="p-4">
                        <div className="flex flex-col sm:flex-row gap-4">
                            <form onSubmit={handleSearch} className="relative flex-1 flex gap-2">
                                <div className="relative flex-1">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        placeholder="Search by email or company..."
                                        value={searchInput}
                                        onChange={(e) => setSearchInput(e.target.value)}
                                        className="pl-9 bg-accent border-border"
                                    />
                                </div>
                                <Button type="submit" size="sm">Search</Button>
                            </form>
                            <Select value={roleFilter} onValueChange={setRoleFilter}>
                                <SelectTrigger className="w-full sm:w-36 bg-accent border-border">
                                    <SelectValue placeholder="Role" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Roles</SelectItem>
                                    <SelectItem value="contractor">Contractor</SelectItem>
                                    <SelectItem value="moderator">Moderator</SelectItem>
                                    <SelectItem value="admin">Admin</SelectItem>
                                </SelectContent>
                            </Select>
                            <Select value={statusFilter} onValueChange={setStatusFilter}>
                                <SelectTrigger className="w-full sm:w-40 bg-accent border-border">
                                    <SelectValue placeholder="Status" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Status</SelectItem>
                                    <SelectItem value="active">Active</SelectItem>
                                    <SelectItem value="deactivated">Deactivated</SelectItem>
                                    <SelectItem value="suspended">Suspended</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </CardContent>
                </Card>

                {/* Users List */}
                <Card className="bg-card border-border">
                    <CardHeader className="pb-3">
                        <CardTitle className="font-heading font-bold text-lg flex items-center gap-2">
                            <Users className="h-5 w-5 text-primary" />
                            {filteredUsers.length} Users
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3 max-h-[600px] overflow-y-auto">
                            {filteredUsers.length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    <Users className="h-8 w-8 mx-auto mb-2" />
                                    <p>No users found</p>
                                </div>
                            ) : (
                                filteredUsers.map((user) => (
                                    <div key={user.id} className={`p-4 rounded-lg border ${
                                        user.suspended ? 'bg-red-500/10 border-red-500/30' :
                                        !user.is_active ? 'bg-destructive/5 border-destructive/30' : 
                                        'bg-accent/30 border-border'
                                    }`}>
                                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                            <div className="flex items-center gap-3">
                                                <RankBadge rank={user.rank_name || 'Broom Pusher'} size="sm" />
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <p className="font-medium text-foreground truncate">{user.company_name}</p>
                                                        {user.suspended && <Badge className="text-xs bg-red-600">Suspended</Badge>}
                                                        {!user.is_active && !user.suspended && <Badge variant="destructive" className="text-xs">Deactivated</Badge>}
                                                        {user.moderation_strikes > 0 && (
                                                            <Badge variant="outline" className="text-xs text-amber-500 border-amber-500">
                                                                <AlertTriangle className="h-3 w-3 mr-1" />
                                                                {user.moderation_strikes} strike{user.moderation_strikes > 1 ? 's' : ''}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <p className="text-sm text-muted-foreground truncate">{user.email}</p>
                                                    <p className="text-xs text-muted-foreground mt-1">
                                                        {user.contractor_trade} • {user.rank_points || 0} pts • {user.approved_reviews_count || 0} reviews • Joined {formatDate(user.created_at)}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Select value={user.role} onValueChange={(val) => handleUpdateRole(user.id, val)}>
                                                    <SelectTrigger className="w-32 bg-accent border-border">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="contractor">Contractor</SelectItem>
                                                        <SelectItem value="moderator">Moderator</SelectItem>
                                                        <SelectItem value="admin">Admin</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleAddStrike(user.id)}
                                                    title="Add Strike"
                                                >
                                                    <AlertCircle className="h-4 w-4" />
                                                </Button>
                                                {user.moderation_strikes > 0 && (
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => handleRemoveStrike(user.id)}
                                                        title="Remove Strike"
                                                    >
                                                        <History className="h-4 w-4" />
                                                    </Button>
                                                )}
                                                <Button
                                                    variant={user.is_active ? "outline" : "default"}
                                                    size="sm"
                                                    onClick={() => setActionDialog({ open: true, action: user.is_active ? 'deactivate' : 'reactivate', user })}
                                                >
                                                    {user.is_active ? <Ban className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Pagination */}
                        {pagination.total_pages > 1 && (
                            <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
                                <p className="text-sm text-muted-foreground">
                                    Page {pagination.page} of {pagination.total_pages} ({pagination.total} total)
                                </p>
                                <div className="flex gap-2">
                                    <Button 
                                        size="sm" 
                                        variant="outline" 
                                        disabled={pagination.page <= 1 || loading}
                                        onClick={() => loadUsers(pagination.page - 1)}
                                    >
                                        <ChevronLeft className="h-4 w-4" />
                                    </Button>
                                    <Button 
                                        size="sm" 
                                        variant="outline"
                                        disabled={pagination.page >= pagination.total_pages || loading}
                                        onClick={() => loadUsers(pagination.page + 1)}
                                    >
                                        <ChevronRight className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Action Dialog */}
            <AlertDialog open={actionDialog.open} onOpenChange={(open) => !open && setActionDialog({ open: false, action: '', user: null })}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {actionDialog.action === 'deactivate' ? 'Deactivate User' : 'Reactivate User'}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {actionDialog.action === 'deactivate' 
                                ? 'This will disable the user account and prevent login. Their reviews and data will be preserved.'
                                : 'This will re-enable the user account and allow login.'}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleToggleStatus(actionDialog.user)}
                            className={actionDialog.action === 'deactivate' ? 'bg-destructive hover:bg-destructive/90' : ''}>
                            {actionDialog.action === 'deactivate' ? 'Deactivate' : 'Reactivate'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </AdminLayout>
    );
};

export default AdminUsers;
